"use client";

import { useMemo, useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  startOfMonth,
  subMonths,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  format,
} from "date-fns";
import CommonHeader from "./common/CommonHeader";
import { DateRangeSelector } from "./reports/DateRangeSelector";
import { ExecutiveSummary } from "./reports/ExecutiveSummary";
import { OperationsDashboard } from "./reports/OperationsDashboard";
import { SubscriptionsDashboard } from "./reports/SubscriptionsDashboard";
import { StaffPerformanceDashboard } from "./reports/StaffPerformanceDashboard";
import { RevenueDashboard } from "./reports/RevenueDashboard";
import { DailyCompletedDashboard } from "./reports/DailyCompletedDashboard";
import { CashCollectionDashboard } from "./reports/CashCollectionDashboard";
import { SalesReportDashboard } from "./reports/SalesReportDashboard";
import { SubscriptionRevenueDashboard } from "./reports/SubscriptionRevenueDashboard";
import { NotCompletedOrdersDashboard } from "./reports/NotCompletedOrdersDashboard";
import { OutstandingPaymentDashboard } from "./reports/OutstandingPaymentDashboard";
import { SubscriptionUsageDashboard } from "./reports/SubscriptionUsageDashboard";
import { MonthlySummaryDashboard } from "./reports/MonthlySummaryDashboard";
import { CustomerWashHistoryDashboard } from "./reports/CustomerWashHistoryDashboard";
import { OfflineBookingDashboard } from "./reports/OfflineBookingDashboard";
import { reportsService } from "@/services/reportsService";
import type {
  ExecutiveSummaryResponse,
  OperationsReportResponse,
  SubscriptionsReportResponse,
  StaffPerformanceReportResponse,
  RevenueReportResponse,
  DailyCompletedReportResponse,
  CashCollectionReportResponse,
  SalesReportResponse,
  SubscriptionRevenueReportResponse,
  NotCompletedOrdersReportResponse,
  OutstandingPaymentReportResponse,
  SubscriptionUsageReportResponse,
  MonthlySummaryReportResponse,
  CustomerWashHistoryReportResponse,
  OfflineBookingReportResponse,
} from "@/services/reportsService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function ReportsAnalytics() {
  const [dateRange, setDateRange] = useState<
    "week" | "month" | "quarter" | "year" | "custom"
  >("month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const handleCustomDateChange = (startDate: string, endDate: string) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  };

  // Customer selection for wash history report
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customers, setCustomers] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Original API data states
  const [executiveSummary, setExecutiveSummary] = useState<
    ExecutiveSummaryResponse["data"] | null
  >(null);
  const [operationsReport, setOperationsReport] = useState<
    OperationsReportResponse["data"] | null
  >(null);
  const [subscriptionsReport, setSubscriptionsReport] = useState<
    SubscriptionsReportResponse["data"] | null
  >(null);
  const [staffPerformanceReport, setStaffPerformanceReport] = useState<
    StaffPerformanceReportResponse["data"] | null
  >(null);
  const [revenueReport, setRevenueReport] = useState<
    RevenueReportResponse["data"] | null
  >(null);

  // New report data states
  const [dailyCompletedReport, setDailyCompletedReport] = useState<
    DailyCompletedReportResponse["data"] | null
  >(null);
  const [cashCollectionReport, setCashCollectionReport] = useState<
    CashCollectionReportResponse["data"] | null
  >(null);
  const [salesReport, setSalesReport] = useState<
    SalesReportResponse["data"] | null
  >(null);
  const [subscriptionRevenueReport, setSubscriptionRevenueReport] = useState<
    SubscriptionRevenueReportResponse["data"] | null
  >(null);
  const [notCompletedOrdersReport, setNotCompletedOrdersReport] = useState<
    NotCompletedOrdersReportResponse["data"] | null
  >(null);
  const [outstandingPaymentReport, setOutstandingPaymentReport] = useState<
    OutstandingPaymentReportResponse["data"] | null
  >(null);
  const [subscriptionUsageReport, setSubscriptionUsageReport] = useState<
    SubscriptionUsageReportResponse["data"] | null
  >(null);
  const [monthlySummaryReport, setMonthlySummaryReport] = useState<
    MonthlySummaryReportResponse["data"] | null
  >(null);
  const [customerWashHistoryReport, setCustomerWashHistoryReport] = useState<
    CustomerWashHistoryReportResponse["data"] | null
  >(null);
  const [offlineBookingReport, setOfflineBookingReport] = useState<
    OfflineBookingReportResponse["data"] | null
  >(null);

  // Original loading states
  const [loadingExecutiveSummary, setLoadingExecutiveSummary] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [loadingStaffPerformance, setLoadingStaffPerformance] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // New loading states
  const [loadingDailyCompleted, setLoadingDailyCompleted] = useState(false);
  const [loadingCashCollection, setLoadingCashCollection] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [loadingSubscriptionRevenue, setLoadingSubscriptionRevenue] =
    useState(false);
  const [loadingNotCompletedOrders, setLoadingNotCompletedOrders] =
    useState(false);
  const [loadingOutstandingPayment, setLoadingOutstandingPayment] =
    useState(false);
  const [loadingSubscriptionUsage, setLoadingSubscriptionUsage] =
    useState(false);
  const [loadingMonthlySummary, setLoadingMonthlySummary] = useState(false);
  const [loadingCustomerWashHistory, setLoadingCustomerWashHistory] =
    useState(false);
  const [loadingOfflineBooking, setLoadingOfflineBooking] = useState(false);

  // Original error states
  const [errorExecutiveSummary, setErrorExecutiveSummary] = useState<
    string | null
  >(null);
  const [errorOperations, setErrorOperations] = useState<string | null>(null);
  const [errorSubscriptions, setErrorSubscriptions] = useState<string | null>(
    null,
  );
  const [errorStaffPerformance, setErrorStaffPerformance] = useState<
    string | null
  >(null);
  const [errorRevenue, setErrorRevenue] = useState<string | null>(null);

  // New error states
  const [errorDailyCompleted, setErrorDailyCompleted] = useState<string | null>(
    null,
  );
  const [errorCashCollection, setErrorCashCollection] = useState<string | null>(
    null,
  );
  const [errorSales, setErrorSales] = useState<string | null>(null);
  const [errorSubscriptionRevenue, setErrorSubscriptionRevenue] = useState<
    string | null
  >(null);
  const [errorNotCompletedOrders, setErrorNotCompletedOrders] = useState<
    string | null
  >(null);
  const [errorOutstandingPayment, setErrorOutstandingPayment] = useState<
    string | null
  >(null);
  const [errorSubscriptionUsage, setErrorSubscriptionUsage] = useState<
    string | null
  >(null);
  const [errorMonthlySummary, setErrorMonthlySummary] = useState<string | null>(
    null,
  );
  const [errorCustomerWashHistory, setErrorCustomerWashHistory] = useState<
    string | null
  >(null);
  const [errorOfflineBooking, setErrorOfflineBooking] = useState<string | null>(
    null,
  );

  // Helper function to get date range
  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case "week":
        return { start: startOfWeek(now), end: endOfWeek(now) };
      case "month":
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case "quarter":
        return { start: subMonths(startOfMonth(now), 2), end: endOfMonth(now) };
      case "year":
        return {
          start: subMonths(startOfMonth(now), 11),
          end: endOfMonth(now),
        };
      case "custom":
        if (customStartDate && customEndDate) {
          return {
            start: new Date(customStartDate),
            end: new Date(customEndDate),
          };
        }
        return { start: startOfMonth(now), end: endOfMonth(now) };
      default:
        return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  };

  const dateRangeValue = useMemo(
    () => getDateRange(),
    [dateRange, customStartDate, customEndDate],
  );

  // Convert Date to YYYY-MM-DD string
  const formatDateForAPI = (date: Date): string => {
    return format(date, "yyyy-MM-dd");
  };

  // Fetch Executive Summary (single date - today)
  useEffect(() => {
    const fetchExecutiveSummary = async () => {
      setLoadingExecutiveSummary(true);
      setErrorExecutiveSummary(null);
      try {
        const today = formatDateForAPI(new Date());
        const response = await reportsService.getExecutiveSummary(today);
        if (response.status === "success") {
          setExecutiveSummary(response.data);
        } else {
          setErrorExecutiveSummary(
            response.message || "Failed to fetch executive summary",
          );
        }
      } catch (error: any) {
        setErrorExecutiveSummary(
          error.message || "Failed to fetch executive summary",
        );
      } finally {
        setLoadingExecutiveSummary(false);
      }
    };

    fetchExecutiveSummary();
  }, []); // Only fetch once on mount

  // Fetch Operations Report
  useEffect(() => {
    const fetchOperations = async () => {
      setLoadingOperations(true);
      setErrorOperations(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getOperationsReport(params);
        if (response.status === "success") {
          setOperationsReport(response.data);
        } else {
          setErrorOperations(
            response.message || "Failed to fetch operations report",
          );
        }
      } catch (error: any) {
        setErrorOperations(
          error.message || "Failed to fetch operations report",
        );
      } finally {
        setLoadingOperations(false);
      }
    };

    fetchOperations();
  }, [dateRangeValue]);

  // Fetch Subscriptions Report
  useEffect(() => {
    const fetchSubscriptions = async () => {
      setLoadingSubscriptions(true);
      setErrorSubscriptions(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getSubscriptionsReport(params);
        if (response.status === "success") {
          setSubscriptionsReport(response.data);
        } else {
          setErrorSubscriptions(
            response.message || "Failed to fetch subscriptions report",
          );
        }
      } catch (error: any) {
        setErrorSubscriptions(
          error.message || "Failed to fetch subscriptions report",
        );
      } finally {
        setLoadingSubscriptions(false);
      }
    };

    fetchSubscriptions();
  }, [dateRangeValue]);

  // Fetch Staff Performance Report
  useEffect(() => {
    const fetchStaffPerformance = async () => {
      setLoadingStaffPerformance(true);
      setErrorStaffPerformance(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getStaffPerformanceReport(params);
        if (response.status === "success") {
          setStaffPerformanceReport(response.data);
        } else {
          setErrorStaffPerformance(
            response.message || "Failed to fetch staff performance report",
          );
        }
      } catch (error: any) {
        setErrorStaffPerformance(
          error.message || "Failed to fetch staff performance report",
        );
      } finally {
        setLoadingStaffPerformance(false);
      }
    };

    fetchStaffPerformance();
  }, [dateRangeValue]);

  // Fetch Revenue Report
  useEffect(() => {
    const fetchRevenue = async () => {
      setLoadingRevenue(true);
      setErrorRevenue(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getRevenueReport(params);
        if (response.status === "success") {
          setRevenueReport(response.data);
        } else {
          setErrorRevenue(response.message || "Failed to fetch revenue report");
        }
      } catch (error: any) {
        setErrorRevenue(error.message || "Failed to fetch revenue report");
      } finally {
        setLoadingRevenue(false);
      }
    };

    fetchRevenue();
  }, [dateRangeValue]);

  // Fetch Daily Completed Report
  useEffect(() => {
    const fetchDailyCompleted = async () => {
      setLoadingDailyCompleted(true);
      setErrorDailyCompleted(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getDailyCompletedReport(params);
        if (response.status === "success") {
          setDailyCompletedReport(response.data);
        } else {
          setErrorDailyCompleted(
            response.message || "Failed to fetch daily completed report",
          );
        }
      } catch (error: any) {
        setErrorDailyCompleted(
          error.message || "Failed to fetch daily completed report",
        );
      } finally {
        setLoadingDailyCompleted(false);
      }
    };

    fetchDailyCompleted();
  }, [dateRangeValue]);

  // Fetch Cash Collection Report
  useEffect(() => {
    const fetchCashCollection = async () => {
      setLoadingCashCollection(true);
      setErrorCashCollection(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getCashCollectionReport(params);
        if (response.status === "success") {
          setCashCollectionReport(response.data);
        } else {
          setErrorCashCollection(
            response.message || "Failed to fetch cash collection report",
          );
        }
      } catch (error: any) {
        setErrorCashCollection(
          error.message || "Failed to fetch cash collection report",
        );
      } finally {
        setLoadingCashCollection(false);
      }
    };

    fetchCashCollection();
  }, [dateRangeValue]);

  // Fetch Sales Report
  useEffect(() => {
    const fetchSales = async () => {
      setLoadingSales(true);
      setErrorSales(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getSalesReport(params);
        if (response.status === "success") {
          setSalesReport(response.data);
        } else {
          setErrorSales(response.message || "Failed to fetch sales report");
        }
      } catch (error: any) {
        setErrorSales(error.message || "Failed to fetch sales report");
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSales();
  }, [dateRangeValue]);

  // Fetch Subscription Revenue Report
  useEffect(() => {
    const fetchSubscriptionRevenue = async () => {
      setLoadingSubscriptionRevenue(true);
      setErrorSubscriptionRevenue(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response =
          await reportsService.getSubscriptionRevenueReport(params);
        if (response.status === "success") {
          setSubscriptionRevenueReport(response.data);
        } else {
          setErrorSubscriptionRevenue(
            response.message || "Failed to fetch subscription revenue report",
          );
        }
      } catch (error: any) {
        setErrorSubscriptionRevenue(
          error.message || "Failed to fetch subscription revenue report",
        );
      } finally {
        setLoadingSubscriptionRevenue(false);
      }
    };

    fetchSubscriptionRevenue();
  }, [dateRangeValue]);

  // Fetch Not Completed Orders Report
  useEffect(() => {
    const fetchNotCompletedOrders = async () => {
      setLoadingNotCompletedOrders(true);
      setErrorNotCompletedOrders(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response =
          await reportsService.getNotCompletedOrdersReport(params);
        if (response.status === "success") {
          setNotCompletedOrdersReport(response.data);
        } else {
          setErrorNotCompletedOrders(
            response.message || "Failed to fetch not completed orders report",
          );
        }
      } catch (error: any) {
        setErrorNotCompletedOrders(
          error.message || "Failed to fetch not completed orders report",
        );
      } finally {
        setLoadingNotCompletedOrders(false);
      }
    };

    fetchNotCompletedOrders();
  }, [dateRangeValue]);

  // Fetch Outstanding Payment Report
  useEffect(() => {
    const fetchOutstandingPayment = async () => {
      setLoadingOutstandingPayment(true);
      setErrorOutstandingPayment(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response =
          await reportsService.getOutstandingPaymentReport(params);
        if (response.status === "success") {
          setOutstandingPaymentReport(response.data);
        } else {
          setErrorOutstandingPayment(
            response.message || "Failed to fetch outstanding payment report",
          );
        }
      } catch (error: any) {
        setErrorOutstandingPayment(
          error.message || "Failed to fetch outstanding payment report",
        );
      } finally {
        setLoadingOutstandingPayment(false);
      }
    };

    fetchOutstandingPayment();
  }, [dateRangeValue]);

  // Fetch Subscription Usage Report
  useEffect(() => {
    const fetchSubscriptionUsage = async () => {
      setLoadingSubscriptionUsage(true);
      setErrorSubscriptionUsage(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response =
          await reportsService.getSubscriptionUsageReport(params);
        if (response.status === "success") {
          setSubscriptionUsageReport(response.data);
          // Extract unique customers for the wash history dropdown
          const uniqueCustomers = response.data.customers.map((c) => ({
            id: c.customerId,
            name: c.customerName,
          }));
          setCustomers(uniqueCustomers);
        } else {
          setErrorSubscriptionUsage(
            response.message || "Failed to fetch subscription usage report",
          );
        }
      } catch (error: any) {
        setErrorSubscriptionUsage(
          error.message || "Failed to fetch subscription usage report",
        );
      } finally {
        setLoadingSubscriptionUsage(false);
      }
    };

    fetchSubscriptionUsage();
  }, [dateRangeValue]);

  // Fetch Monthly Summary Report
  useEffect(() => {
    const fetchMonthlySummary = async () => {
      setLoadingMonthlySummary(true);
      setErrorMonthlySummary(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getMonthlySummaryReport(params);
        if (response.status === "success") {
          setMonthlySummaryReport(response.data);
        } else {
          setErrorMonthlySummary(
            response.message || "Failed to fetch monthly summary report",
          );
        }
      } catch (error: any) {
        setErrorMonthlySummary(
          error.message || "Failed to fetch monthly summary report",
        );
      } finally {
        setLoadingMonthlySummary(false);
      }
    };

    fetchMonthlySummary();
  }, [dateRangeValue]);

  // Fetch Customer Wash History Report (when customer is selected)
  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerWashHistoryReport(null);
      return;
    }

    const fetchCustomerWashHistory = async () => {
      setLoadingCustomerWashHistory(true);
      setErrorCustomerWashHistory(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getCustomerWashHistoryReport(
          selectedCustomerId,
          params,
        );
        if (response.status === "success") {
          setCustomerWashHistoryReport(response.data);
        } else {
          setErrorCustomerWashHistory(
            response.message || "Failed to fetch customer wash history",
          );
        }
      } catch (error: any) {
        setErrorCustomerWashHistory(
          error.message || "Failed to fetch customer wash history",
        );
      } finally {
        setLoadingCustomerWashHistory(false);
      }
    };

    fetchCustomerWashHistory();
  }, [selectedCustomerId, dateRangeValue]);

  // Fetch Offline Booking Report
  useEffect(() => {
    const fetchOfflineBooking = async () => {
      setLoadingOfflineBooking(true);
      setErrorOfflineBooking(null);
      try {
        const params = {
          startDate: formatDateForAPI(dateRangeValue.start),
          endDate: formatDateForAPI(dateRangeValue.end),
        };
        const response = await reportsService.getOfflineBookingReport(params);
        if (response.status === "success") {
          setOfflineBookingReport(response.data);
        } else {
          setErrorOfflineBooking(
            response.message || "Failed to fetch offline booking report",
          );
        }
      } catch (error: any) {
        setErrorOfflineBooking(
          error.message || "Failed to fetch offline booking report",
        );
      } finally {
        setLoadingOfflineBooking(false);
      }
    };

    fetchOfflineBooking();
  }, [dateRangeValue]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <CommonHeader
          title="Reports & Analytics"
          description="Decision-driven business insights and analytics"
        />
        <DateRangeSelector
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomDateChange={handleCustomDateChange}
        />
      </div>

      {/* Executive Summary - Always visible at top */}
      {loadingExecutiveSummary ? (
        <div className="text-center py-8">Loading executive summary...</div>
      ) : errorExecutiveSummary ? (
        <div className="text-center py-8 text-red-600">
          Error: {errorExecutiveSummary}
        </div>
      ) : executiveSummary ? (
        <ExecutiveSummary data={executiveSummary} />
      ) : null}

      {/* Detailed Reports in Tabs */}
      <Tabs defaultValue="operations" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="staff">Staff</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="daily-completed">Daily Completed</TabsTrigger>
          <TabsTrigger value="cash-collection">Cash Collection</TabsTrigger>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="subscription-revenue">Sub Revenue</TabsTrigger>
          <TabsTrigger value="not-completed">Not Completed</TabsTrigger>
          <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="subscription-usage">Sub Usage</TabsTrigger>
          <TabsTrigger value="monthly-summary">Monthly Summary</TabsTrigger>
          <TabsTrigger value="wash-history">Wash History</TabsTrigger>
          <TabsTrigger value="offline-booking">Offline Booking</TabsTrigger>
        </TabsList>

        {/* OPERATIONS DASHBOARD */}
        <TabsContent value="operations" className="space-y-4">
          {loadingOperations ? (
            <div className="text-center py-8">Loading operations report...</div>
          ) : errorOperations ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorOperations}
            </div>
          ) : operationsReport ? (
            <OperationsDashboard data={operationsReport} />
          ) : null}
        </TabsContent>

        {/* SUBSCRIPTIONS DASHBOARD */}
        <TabsContent value="subscriptions" className="space-y-4">
          {loadingSubscriptions ? (
            <div className="text-center py-8">
              Loading subscriptions report...
            </div>
          ) : errorSubscriptions ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorSubscriptions}
            </div>
          ) : subscriptionsReport ? (
            <SubscriptionsDashboard data={subscriptionsReport} />
          ) : null}
        </TabsContent>

        {/* STAFF PERFORMANCE DASHBOARD */}
        <TabsContent value="staff" className="space-y-4">
          {loadingStaffPerformance ? (
            <div className="text-center py-8">
              Loading staff performance report...
            </div>
          ) : errorStaffPerformance ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorStaffPerformance}
            </div>
          ) : staffPerformanceReport ? (
            <StaffPerformanceDashboard data={staffPerformanceReport} />
          ) : null}
        </TabsContent>

        {/* REVENUE DASHBOARD */}
        <TabsContent value="revenue" className="space-y-4">
          {loadingRevenue ? (
            <div className="text-center py-8">Loading revenue report...</div>
          ) : errorRevenue ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorRevenue}
            </div>
          ) : revenueReport ? (
            <RevenueDashboard data={revenueReport} />
          ) : null}
        </TabsContent>

        {/* DAILY COMPLETED ORDERS DASHBOARD */}
        <TabsContent value="daily-completed" className="space-y-4">
          {loadingDailyCompleted ? (
            <div className="text-center py-8">
              Loading daily completed report...
            </div>
          ) : errorDailyCompleted ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorDailyCompleted}
            </div>
          ) : dailyCompletedReport ? (
            <DailyCompletedDashboard data={dailyCompletedReport} />
          ) : null}
        </TabsContent>

        {/* CASH COLLECTION DASHBOARD */}
        <TabsContent value="cash-collection" className="space-y-4">
          {loadingCashCollection ? (
            <div className="text-center py-8">
              Loading cash collection report...
            </div>
          ) : errorCashCollection ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorCashCollection}
            </div>
          ) : cashCollectionReport ? (
            <CashCollectionDashboard data={cashCollectionReport} />
          ) : null}
        </TabsContent>

        {/* SALES REPORT DASHBOARD */}
        <TabsContent value="sales" className="space-y-4">
          {loadingSales ? (
            <div className="text-center py-8">Loading sales report...</div>
          ) : errorSales ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorSales}
            </div>
          ) : salesReport ? (
            <SalesReportDashboard data={salesReport} />
          ) : null}
        </TabsContent>

        {/* SUBSCRIPTION REVENUE DASHBOARD */}
        <TabsContent value="subscription-revenue" className="space-y-4">
          {loadingSubscriptionRevenue ? (
            <div className="text-center py-8">
              Loading subscription revenue report...
            </div>
          ) : errorSubscriptionRevenue ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorSubscriptionRevenue}
            </div>
          ) : subscriptionRevenueReport ? (
            <SubscriptionRevenueDashboard data={subscriptionRevenueReport} />
          ) : null}
        </TabsContent>

        {/* NOT COMPLETED ORDERS DASHBOARD */}
        <TabsContent value="not-completed" className="space-y-4">
          {loadingNotCompletedOrders ? (
            <div className="text-center py-8">
              Loading not completed orders report...
            </div>
          ) : errorNotCompletedOrders ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorNotCompletedOrders}
            </div>
          ) : notCompletedOrdersReport ? (
            <NotCompletedOrdersDashboard data={notCompletedOrdersReport} />
          ) : null}
        </TabsContent>

        {/* OUTSTANDING PAYMENT DASHBOARD */}
        <TabsContent value="outstanding" className="space-y-4">
          {loadingOutstandingPayment ? (
            <div className="text-center py-8">
              Loading outstanding payment report...
            </div>
          ) : errorOutstandingPayment ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorOutstandingPayment}
            </div>
          ) : outstandingPaymentReport ? (
            <OutstandingPaymentDashboard data={outstandingPaymentReport} />
          ) : null}
        </TabsContent>

        {/* SUBSCRIPTION USAGE DASHBOARD */}
        <TabsContent value="subscription-usage" className="space-y-4">
          {loadingSubscriptionUsage ? (
            <div className="text-center py-8">
              Loading subscription usage report...
            </div>
          ) : errorSubscriptionUsage ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorSubscriptionUsage}
            </div>
          ) : subscriptionUsageReport ? (
            <SubscriptionUsageDashboard data={subscriptionUsageReport} />
          ) : null}
        </TabsContent>

        {/* MONTHLY SUMMARY DASHBOARD */}
        <TabsContent value="monthly-summary" className="space-y-4">
          {loadingMonthlySummary ? (
            <div className="text-center py-8">
              Loading monthly summary report...
            </div>
          ) : errorMonthlySummary ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorMonthlySummary}
            </div>
          ) : monthlySummaryReport ? (
            <MonthlySummaryDashboard data={monthlySummaryReport} />
          ) : null}
        </TabsContent>

        {/* CUSTOMER WASH HISTORY DASHBOARD */}
        <TabsContent value="wash-history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Select Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedCustomerId}
                onValueChange={setSelectedCustomerId}
              >
                <SelectTrigger className="w-full md:w-80">
                  <SelectValue placeholder="Select a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {!selectedCustomerId ? (
            <div className="text-center py-8 text-muted-foreground">
              Please select a customer to view their wash history
            </div>
          ) : loadingCustomerWashHistory ? (
            <div className="text-center py-8">Loading wash history...</div>
          ) : errorCustomerWashHistory ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorCustomerWashHistory}
            </div>
          ) : customerWashHistoryReport ? (
            <CustomerWashHistoryDashboard data={customerWashHistoryReport} />
          ) : null}
        </TabsContent>

        {/* OFFLINE BOOKING DASHBOARD */}
        <TabsContent value="offline-booking" className="space-y-4">
          {loadingOfflineBooking ? (
            <div className="text-center py-8">
              Loading offline booking report...
            </div>
          ) : errorOfflineBooking ? (
            <div className="text-center py-8 text-red-600">
              Error: {errorOfflineBooking}
            </div>
          ) : offlineBookingReport ? (
            <OfflineBookingDashboard data={offlineBookingReport} />
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
